import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, History as HistoryIcon, Loader2, CheckCircle2, XCircle, TrendingUp, TrendingDown, Globe, Calendar, Download, ChevronDown, Columns, Settings2, Play, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Only India is supported - other countries removed per user request
const COUNTRIES = [
  { id: "india", name: "India", flag: "🇮🇳", currency: "₹", exchange: "NSE" },
];

// Define all available columns
const ALL_COLUMNS = [
  { id: "stock", label: "Stock", default: true },
  { id: "shares", label: "Shares", default: true },
  { id: "strategy", label: "Strategy", default: true },
  { id: "startPrice", label: "Start Price", default: true },
  { id: "startDate", label: "Start Date", default: false },
  { id: "predicted", label: "Predicted", default: true },
  { id: "summary", label: "Prediction Summary", default: true },
  { id: "evalDate", label: "Eval Date", default: false },
  { id: "actual", label: "Actual", default: true },
  { id: "actualDate", label: "Actual Date", default: false },
  { id: "commission", label: "Commission", default: false },
  { id: "grossPL", label: "Gross P/L", default: true },
  { id: "netPL", label: "Net P/L", default: true },
  { id: "result", label: "Result", default: true },
];

export default function PredictionHistory() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  const [commissionPct, setCommissionPct] = useState(0.2);
  const [stats, setStats] = useState({ evaluated: 0 });
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || "india");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]); // Multi-select dates
  const [selectAll, setSelectAll] = useState(true);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isEvaluating, setIsEvaluating] = useState(false);
  
  // Column visibility state - initialize from localStorage or defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('historyVisibleColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
      }
    }
    return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
  });

  const currentCountry = COUNTRIES.find(c => c.id === selectedCountry) || COUNTRIES[0];

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('historyVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Fetch available dates for the checkboxes
  const fetchAvailableDates = async (country = selectedCountry) => {
    try {
      const res = await axios.get(`${API}/predictions/dates?country=${country}&evaluated_only=true`);
      const dates = res.data.dates || [];
      setAvailableDates(dates);
      // Initially select all dates
      setSelectedDates(dates);
      setSelectAll(true);
    } catch (err) {
      console.error("Failed to fetch dates:", err);
    }
  };

  const fetchHistory = async (country = selectedCountry, dates = selectedDates) => {
    try {
      setLoading(true);
      
      // Fetch evaluated stocks
      let url = `${API}/predictions/history?country=${country}&evaluated_only=true`;
      
      // Add date filter if specific dates selected (not all)
      if (dates.length > 0 && dates.length < availableDates.length) {
        url += `&dates=${dates.join(',')}`;
      }
      
      const res = await axios.get(url);
      
      // Filter to only evaluated stocks (actual_price is not null)
      const evaluatedOnly = (res.data.history || []).filter(
        item => item.actual_price !== null && item.actual_price !== undefined
      );
      
      setHistory(evaluatedOnly);
      setStats({
        evaluated: evaluatedOnly.length
      });
      
      // Also fetch pending count (non-evaluated stocks)
      try {
        const pendingRes = await axios.get(`${API}/predictions/history?country=${country}&limit=500`);
        const allHistory = pendingRes.data.history || [];
        const pending = allHistory.filter(
          item => item.actual_price === null || item.actual_price === undefined
        );
        setPendingCount(pending.length);
      } catch (err) {
        console.error("Failed to fetch pending count:", err);
      }
      
      if (evaluatedOnly.length > 0) {
        toast.success(`${evaluatedOnly.length} evaluated stocks loaded`);
      } else {
        toast.info("No evaluated stocks found");
      }
    } catch (err) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // Format date for display in dropdown
  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format full datetime for table display in IST
  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    // Convert to IST (UTC+5:30)
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) + ' IST';
  };

  // Format date only (no time) in IST
  const formatDateOnly = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', 
      month: 'short' 
    });
  };

  // Force evaluate pending predictions using evaluation date
  const forceEvaluatePending = async () => {
    try {
      setIsEvaluating(true);
      toast.info(`Evaluating ${pendingCount} pending stocks...`);
      
      const res = await axios.post(`${API}/admin/force-evaluate-by-date?country=${selectedCountry}`);
      
      if (res.data.success) {
        toast.success(`Evaluated ${res.data.evaluated_count} stocks (${res.data.skipped_count} skipped, ${res.data.failed_count} failed)`);
        // Refresh history after evaluation
        await fetchHistory(selectedCountry, selectedDates);
      } else {
        toast.error("Evaluation failed");
      }
    } catch (err) {
      toast.error("Force evaluation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsEvaluating(false);
    }
  };

  useEffect(() => {
    fetchAvailableDates(selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    if (availableDates.length > 0) {
      fetchHistory(selectedCountry, selectedDates);
    }
  }, [selectedDates, availableDates]);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setHistory([]);
    setAvailableDates([]);
    setSelectedDates([]);
  };

  // Handle individual date checkbox
  const handleDateToggle = (date) => {
    if (selectedDates.includes(date)) {
      const newSelected = selectedDates.filter(d => d !== date);
      setSelectedDates(newSelected);
      setSelectAll(false);
    } else {
      const newSelected = [...selectedDates, date];
      setSelectedDates(newSelected);
      setSelectAll(newSelected.length === availableDates.length);
    }
  };

  // Handle select all checkbox for dates
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDates([]);
      setSelectAll(false);
    } else {
      setSelectedDates([...availableDates]);
      setSelectAll(true);
    }
  };

  // Handle column visibility toggle
  const handleColumnToggle = (columnId) => {
    if (visibleColumns.includes(columnId)) {
      // Don't allow hiding all columns - keep at least one
      if (visibleColumns.length > 1) {
        setVisibleColumns(visibleColumns.filter(id => id !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };

  // Handle select all columns
  const handleSelectAllColumns = () => {
    if (visibleColumns.length === ALL_COLUMNS.length) {
      // Keep only default columns
      setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id));
    } else {
      setVisibleColumns(ALL_COLUMNS.map(c => c.id));
    }
  };

  // Reset columns to default
  const handleResetColumns = () => {
    setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id));
    toast.success("Columns reset to default");
  };

  // Check if column is visible
  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  // Get dropdown label text
  const getDateDropdownLabel = () => {
    if (selectAll || selectedDates.length === availableDates.length) {
      return `All Dates (${availableDates.length})`;
    } else if (selectedDates.length === 0) {
      return "Select Dates";
    } else if (selectedDates.length === 1) {
      return formatDateLabel(selectedDates[0]);
    } else {
      return `${selectedDates.length} dates selected`;
    }
  };

  // Calculate totals for evaluated stocks using ACTUAL prices only
  const calculateTotals = () => {
    if (history.length === 0) return { totalInvested: 0, totalGross: 0, totalGrossPct: 0, totalCommission: 0, totalNet: 0, totalNetPct: 0, daysCount: 0 };
    
    // Group items by date
    const itemsByDate = {};
    history.forEach(item => {
      const dateKey = item.prediction_date.substring(0, 10);
      if (!itemsByDate[dateKey]) {
        itemsByDate[dateKey] = [];
      }
      itemsByDate[dateKey].push(item);
    });
    
    const daysCount = Object.keys(itemsByDate).length;
    const totalInvested = investmentAmount;
    const totalCommission = (commissionPct / 100) * investmentAmount * daysCount;
    
    let totalGross = 0;
    
    // Calculate profit for each day using ACTUAL prices
    Object.values(itemsByDate).forEach(dayItems => {
      const amountPerStock = investmentAmount / dayItems.length;
      
      dayItems.forEach(item => {
        const startPrice = item.current_price || item.start_price;
        const actualPrice = item.actual_price;
        const shares = amountPerStock / startPrice;
        
        // Calculate profit based on direction using ACTUAL price
        let grossProfit = 0;
        if (item.direction === "Bullish") {
          // BUY: profit = (actual - start) * shares
          grossProfit = shares * (actualPrice - startPrice);
        } else if (item.direction === "Bearish") {
          // SHORT: profit = (start - actual) * shares
          grossProfit = shares * (startPrice - actualPrice);
        }
        totalGross += grossProfit;
      });
    });
    
    const totalNet = totalGross - totalCommission;
    
    return {
      totalInvested: totalInvested,
      totalGross: totalGross,
      totalGrossPct: (totalGross / totalInvested * 100),
      totalCommission: totalCommission,
      totalNet: totalNet,
      totalNetPct: (totalNet / totalInvested * 100),
      daysCount: daysCount
    };
  };

  const totals = calculateTotals();

  // Download history as CSV
  const downloadCSV = () => {
    if (history.length === 0) {
      toast.error("No data to download");
      return;
    }

    const headers = [
      "Stock",
      "Shares",
      "Strategy",
      "Start Price",
      "Start Date/Time",
      "Predicted Price",
      "Evaluation Date",
      "Actual Price",
      "Actual Date/Time",
      "Commission",
      "Gross Profit",
      "Net Profit"
    ];

    const rows = history.map(item => {
      const displaySymbol = item.symbol.replace(/\.(NS|KA|NP|CM|DU)$/, "");
      const strategy = item.direction === "Bullish" ? "BUY" : "SHORT";
      const startPrice = item.current_price || item.start_price;
      const actualPrice = item.actual_price;
      
      // Calculate per-stock values
      const itemDate = item.prediction_date.substring(0, 10);
      const sameDayItems = history.filter(i => i.prediction_date.substring(0, 10) === itemDate);
      const amountPerStock = investmentAmount / sameDayItems.length;
      const shares = amountPerStock / startPrice;
      const commissionPerStock = (commissionPct / 100) * amountPerStock;
      
      // Calculate profit using ACTUAL price
      let grossProfit = 0;
      if (item.direction === "Bullish") {
        grossProfit = shares * (actualPrice - startPrice);
      } else {
        grossProfit = shares * (startPrice - actualPrice);
      }
      const netProfit = grossProfit - commissionPerStock;

      return [
        displaySymbol,
        shares.toFixed(2),
        strategy,
        startPrice.toFixed(2),
        item.start_price_timestamp || item.prediction_date,
        item.predicted_price.toFixed(2),
        item.evaluation_date || "-",
        actualPrice.toFixed(2),
        item.actual_price_timestamp || "-",
        commissionPerStock.toFixed(2),
        grossProfit.toFixed(2),
        netProfit.toFixed(2)
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `stocksense_evaluated_${currentCountry.id}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Downloaded evaluated stocks CSV");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <HistoryIcon className="h-8 w-8" />
              Evaluated Stocks History
            </h1>
            <p className="text-gray-400 mt-1">
              Stocks with actual prices - calculated using real market data
            </p>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="text-green-400">{stats.evaluated} Evaluated</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Force Evaluate Button - Only show if there are pending stocks on Dashboard */}
            {pendingCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={forceEvaluatePending} 
                      disabled={isEvaluating || pendingCount === 0}
                      variant="destructive"
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="force-evaluate-btn"
                    >
                      {isEvaluating ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Evaluate {pendingCount} Pending
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-900 border-gray-700">
                    <p>{pendingCount} stocks on Dashboard are awaiting evaluation.</p>
                    <p className="text-xs text-gray-400">Click to fetch actual prices and move them here.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Country Selector */}
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-400" />
              <Select value={selectedCountry} onValueChange={handleCountryChange}>
                <SelectTrigger className="w-[180px]" data-testid="country-selector">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      {currentCountry.flag} {currentCountry.name}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.id} value={country.id}>
                      <span className="flex items-center gap-2">
                        {country.flag} {country.name} ({country.exchange})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Dropdown with Checkboxes */}
            {availableDates.length > 0 && (
              <Popover open={dateDropdownOpen} onOpenChange={setDateDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-[180px] justify-between"
                    data-testid="date-dropdown-trigger"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span className="truncate">{getDateDropdownLabel()}</span>
                    </div>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-2" align="start">
                  <div className="space-y-2">
                    {/* Select All Option */}
                    <div 
                      className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer border-b border-gray-700 pb-3"
                      onClick={handleSelectAll}
                    >
                      <Checkbox
                        id="select-all"
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-dates"
                      />
                      <label htmlFor="select-all" className="text-sm font-medium cursor-pointer flex-1">
                        Select All
                      </label>
                      <Badge variant="secondary" className="text-xs">
                        {availableDates.length}
                      </Badge>
                    </div>
                    
                    {/* Individual Date Options */}
                    <div className="max-h-[200px] overflow-y-auto space-y-1">
                      {availableDates.map((date) => (
                        <div 
                          key={date} 
                          className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                          onClick={() => handleDateToggle(date)}
                        >
                          <Checkbox
                            id={`date-${date}`}
                            checked={selectedDates.includes(date)}
                            onCheckedChange={() => handleDateToggle(date)}
                            data-testid={`date-checkbox-${date}`}
                          />
                          <label 
                            htmlFor={`date-${date}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {formatDateLabel(date)}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Column Selector Dropdown */}
            <Popover open={columnDropdownOpen} onOpenChange={setColumnDropdownOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="justify-between"
                  data-testid="column-dropdown-trigger"
                >
                  <div className="flex items-center gap-2">
                    <Columns className="h-4 w-4" />
                    <span>Columns ({visibleColumns.length})</span>
                  </div>
                  <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2" align="end">
                <div className="space-y-2">
                  {/* Header with Select All and Reset */}
                  <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                    <div 
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 rounded p-1"
                      onClick={handleSelectAllColumns}
                    >
                      <Checkbox
                        id="select-all-columns"
                        checked={visibleColumns.length === ALL_COLUMNS.length}
                        onCheckedChange={handleSelectAllColumns}
                        data-testid="select-all-columns"
                      />
                      <label htmlFor="select-all-columns" className="text-sm font-medium cursor-pointer">
                        All
                      </label>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleResetColumns}
                      className="text-xs h-7 px-2"
                    >
                      <Settings2 className="h-3 w-3 mr-1" />
                      Reset
                    </Button>
                  </div>
                  
                  {/* Individual Column Options */}
                  <div className="max-h-[300px] overflow-y-auto space-y-1">
                    {ALL_COLUMNS.map((column) => (
                      <div 
                        key={column.id} 
                        className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                        onClick={() => handleColumnToggle(column.id)}
                      >
                        <Checkbox
                          id={`column-${column.id}`}
                          checked={visibleColumns.includes(column.id)}
                          onCheckedChange={() => handleColumnToggle(column.id)}
                          data-testid={`column-checkbox-${column.id}`}
                        />
                        <label 
                          htmlFor={`column-${column.id}`} 
                          className="text-sm cursor-pointer flex-1"
                        >
                          {column.label}
                        </label>
                        {column.default && (
                          <Badge variant="outline" className="text-xs opacity-50">
                            default
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <Button onClick={() => fetchHistory(selectedCountry, selectedDates)} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
            </Button>
            <Button onClick={downloadCSV} disabled={history.length === 0} variant="outline" data-testid="download-btn">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* Country Info Badge */}
        <Badge className="bg-blue-900/50 border-blue-700">
          {currentCountry.flag} {currentCountry.exchange} • {currentCountry.currency} {currentCountry.name}
        </Badge>

        {/* Investment Settings */}
        <Card className="p-4 bg-blue-900/20 border-blue-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Investment Amount (Total)</label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400 text-sm">{currentCountry.currency}</span>
                <Input
                  type="text"
                  value={investmentAmount.toLocaleString('en-IN')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (!isNaN(value) && value !== '') {
                      setInvestmentAmount(Number(value));
                    } else if (value === '') {
                      setInvestmentAmount(0);
                    }
                  }}
                  className="pl-9"
                  placeholder="10,00,000"
                  data-testid="investment-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Commission %</label>
              <Input
                type="number"
                step="0.1"
                value={commissionPct}
                onChange={(e) => setCommissionPct(Number(e.target.value))}
                placeholder="0.2"
                data-testid="commission-input"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm">
                <p className="text-gray-400">Evaluated Stocks</p>
                <p className="text-xl font-bold">{history.length}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Evaluated Stocks Summary Card */}
        {history.length > 0 && (
          <Card className="p-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700">
            <h3 className="text-lg font-semibold mb-4">
              Evaluated Stocks
              {totals.daysCount > 1 && <span className="text-sm font-normal text-gray-400 ml-2">({totals.daysCount} trading days - same capital reinvested)</span>}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-400">Investment Capital</p>
                <p className="text-2xl font-bold">{currentCountry.currency}{totals.totalInvested.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Gross Profit</p>
                <p className={`text-2xl font-bold ${totals.totalGross >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.totalGross >= 0 ? '+' : ''}{currentCountry.currency}{totals.totalGross.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <p className={`text-xs ${totals.totalGrossPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.totalGrossPct >= 0 ? '+' : ''}{totals.totalGrossPct.toFixed(2)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Commission ({commissionPct}%)</p>
                <p className="text-2xl font-bold text-red-400">
                  -{currentCountry.currency}{totals.totalCommission.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-500">{totals.daysCount} day(s) × {commissionPct}%</p>
              </div>
              <div className="border-l border-gray-600 pl-4">
                <p className="text-sm text-gray-400">Net Profit</p>
                <p className={`text-3xl font-bold ${totals.totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.totalNet >= 0 ? '+' : ''}{currentCountry.currency}{totals.totalNet.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <p className={`text-sm ${totals.totalNetPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.totalNetPct >= 0 ? '+' : ''}{totals.totalNetPct.toFixed(2)}% return
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Evaluated Stocks Table */}
        {history.length === 0 && !loading ? (
          <Card className="p-8 text-center">
            <HistoryIcon className="h-12 w-12 mx-auto text-gray-500 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No Evaluated Stocks for {currentCountry.name}</h3>
            <p className="text-gray-400 mb-4">
              Stocks will appear here after they have been evaluated with actual prices.
            </p>
            <Link to="/">
              <Button>Go to Dashboard</Button>
            </Link>
          </Card>
        ) : (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">
              Evaluated Stocks - {currentCountry.name}
              {selectedDates.length > 0 && selectedDates.length < availableDates.length && (
                <span className="text-sm font-normal text-gray-400 ml-2">
                  ({selectedDates.length} date{selectedDates.length > 1 ? 's' : ''} selected)
                </span>
              )}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b-2 border-gray-700">
                    {isColumnVisible("stock") && <th className="text-left p-2 font-semibold">Stock</th>}
                    {isColumnVisible("shares") && <th className="text-right p-2 font-semibold">Shares</th>}
                    {isColumnVisible("strategy") && <th className="text-center p-2 font-semibold">Strategy</th>}
                    {isColumnVisible("startPrice") && <th className="text-right p-2 font-semibold">Start Price</th>}
                    {isColumnVisible("startDate") && <th className="text-center p-2 font-semibold">Start Date</th>}
                    {isColumnVisible("predicted") && <th className="text-right p-2 font-semibold">Predicted</th>}
                    {isColumnVisible("summary") && <th className="text-left p-2 font-semibold">Summary</th>}
                    {isColumnVisible("evalDate") && <th className="text-center p-2 font-semibold">Eval Date</th>}
                    {isColumnVisible("actual") && <th className="text-right p-2 font-semibold">Actual</th>}
                    {isColumnVisible("actualDate") && <th className="text-center p-2 font-semibold">Actual Date</th>}
                    {isColumnVisible("commission") && <th className="text-right p-2 font-semibold">Commission</th>}
                    {isColumnVisible("grossPL") && <th className="text-right p-2 font-semibold">Gross P/L</th>}
                    {isColumnVisible("netPL") && <th className="text-right p-2 font-semibold">Net P/L</th>}
                    {isColumnVisible("result") && <th className="text-center p-2 font-semibold">Result</th>}
                  </tr>
                </thead>
                <tbody>
                  {history.map((item, idx) => {
                    const strategy = item.direction === "Bullish" ? "BUY" : "SHORT";
                    const displaySymbol = item.symbol.replace(/\.(NS|KA|NP|CM|DU)$/, "");
                    const startPrice = item.current_price || item.start_price;
                    const actualPrice = item.actual_price;
                    const predictedPrice = item.predicted_price;
                    
                    // Calculate investment per stock for this day
                    const itemDate = item.prediction_date.substring(0, 10);
                    const sameDayItems = history.filter(i => i.prediction_date.substring(0, 10) === itemDate);
                    const stocksOnDay = sameDayItems.length || 1;
                    const amountPerStock = investmentAmount / stocksOnDay;
                    const shares = amountPerStock / startPrice;
                    
                    // Commission per stock
                    const commissionPerStock = (commissionPct / 100) * amountPerStock;
                    
                    // Calculate profit using ACTUAL price only
                    let grossPL = 0;
                    if (item.direction === "Bullish") {
                      // BUY: profit = (actual - start) * shares
                      grossPL = shares * (actualPrice - startPrice);
                    } else {
                      // SHORT: profit = (start - actual) * shares
                      grossPL = shares * (startPrice - actualPrice);
                    }
                    const netPL = grossPL - commissionPerStock;
                    const isProfit = netPL >= 0;

                    // Calculate target achievement percentage
                    // For BUY: target diff = predicted - start, actual diff = actual - start
                    // For SHORT: target diff = start - predicted, actual diff = start - actual
                    let targetDiff = 0;
                    let actualDiff = 0;
                    if (item.direction === "Bullish") {
                      targetDiff = predictedPrice - startPrice;
                      actualDiff = actualPrice - startPrice;
                    } else {
                      targetDiff = startPrice - predictedPrice;
                      actualDiff = startPrice - actualPrice;
                    }
                    
                    // Calculate percentage of target achieved (only if moving in correct direction)
                    let targetAchievedPct = 0;
                    if (targetDiff !== 0 && actualDiff > 0) {
                      targetAchievedPct = (actualDiff / targetDiff) * 100;
                    }
                    
                    // Determine result status
                    // TARGET: 100% or more achieved (or target_reached flag)
                    // PARTIAL: 50% to 99% achieved in correct direction
                    // PROFIT: profitable but less than 50% of target
                    // LOSS: not profitable
                    const isPartialTarget = targetAchievedPct >= 50 && targetAchievedPct < 100 && !item.target_reached;

                    return (
                      <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/50">
                        {isColumnVisible("stock") && (
                          <td className="p-2">
                            <div className="font-bold">{displaySymbol}</div>
                            {item.target_reached && (
                              <div className="text-xs text-green-400">🎯 Target</div>
                            )}
                            {isPartialTarget && (
                              <div className="text-xs text-yellow-400">📊 {targetAchievedPct.toFixed(0)}% achieved</div>
                            )}
                          </td>
                        )}
                        {isColumnVisible("shares") && (
                          <td className="p-2 text-right">
                            <div className="font-medium text-blue-300">{shares.toFixed(2)}</div>
                          </td>
                        )}
                        {isColumnVisible("strategy") && (
                          <td className="p-2 text-center">
                            <Badge className={strategy === "BUY" ? "bg-green-900/50 border-green-700 text-xs" : "bg-orange-900/50 border-orange-700 text-xs"}>
                              {strategy === "BUY" ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                              {strategy}
                            </Badge>
                          </td>
                        )}
                        {isColumnVisible("startPrice") && (
                          <td className="p-2 text-right">
                            <div className="font-semibold">{currentCountry.currency}{Number(startPrice).toFixed(2)}</div>
                          </td>
                        )}
                        {isColumnVisible("startDate") && (
                          <td className="p-2 text-center">
                            <div className="text-xs text-gray-300">
                              {formatDateTime(item.start_price_timestamp || item.prediction_date)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("predicted") && (
                          <td className="p-2 text-right">
                            <div className="text-gray-400">
                              {currentCountry.currency}{Number(predictedPrice).toFixed(2)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("summary") && (
                          <td className="p-2 text-left">
                            {/* Prediction Summary - simplified for history */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-pointer max-w-[150px]">
                                    <div className="flex flex-wrap gap-1">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        item.direction === 'Bullish' ? 'bg-green-900/50 text-green-400' : 'bg-orange-900/50 text-orange-400'
                                      }`}>
                                        {item.direction === 'Bullish' ? '📈' : '📉'} {item.direction}
                                      </span>
                                      {item.target_reached && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-green-900/50 text-green-400">🎯 Hit</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {((predictedPrice - startPrice) / startPrice * 100).toFixed(1)}% target
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm bg-gray-900 border-gray-700 p-2">
                                  <div className="space-y-2">
                                    <p className="font-bold text-blue-400">📊 Prediction Details</p>
                                    <div className="text-xs space-y-1">
                                      <div>Direction: <span className={item.direction === 'Bullish' ? 'text-green-400' : 'text-orange-400'}>{item.direction}</span></div>
                                      <div>Start: {currentCountry.currency}{startPrice.toFixed(2)}</div>
                                      <div>Predicted: {currentCountry.currency}{predictedPrice.toFixed(2)}</div>
                                      <div>Target %: {((predictedPrice - startPrice) / startPrice * 100).toFixed(2)}%</div>
                                      <div>Actual: {currentCountry.currency}{actualPrice.toFixed(2)}</div>
                                      <div>Actual %: {((actualPrice - startPrice) / startPrice * 100).toFixed(2)}%</div>
                                      <div className="border-t border-gray-700 pt-1 mt-1">
                                        Target Achieved: <span className={targetAchievedPct >= 100 ? 'text-green-400' : targetAchievedPct >= 50 ? 'text-yellow-400' : 'text-red-400'}>
                                          {targetAchievedPct.toFixed(0)}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                        )}
                        {isColumnVisible("evalDate") && (
                          <td className="p-2 text-center">
                            <div className="text-xs text-gray-300">
                              {formatDateOnly(item.evaluation_date)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("actual") && (
                          <td className="p-2 text-right">
                            <div className="font-bold text-blue-400">{currentCountry.currency}{Number(actualPrice).toFixed(2)}</div>
                          </td>
                        )}
                        {isColumnVisible("actualDate") && (
                          <td className="p-2 text-center">
                            <div className="text-xs text-gray-300">
                              {formatDateTime(item.actual_price_timestamp)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("commission") && (
                          <td className="p-2 text-right">
                            <div className="text-red-400 text-sm">
                              -{currentCountry.currency}{commissionPerStock.toFixed(0)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("grossPL") && (
                          <td className="p-2 text-right">
                            <div className={`font-semibold ${grossPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {grossPL >= 0 ? '+' : ''}{currentCountry.currency}{grossPL.toFixed(0)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("netPL") && (
                          <td className="p-2 text-right">
                            <div className={`font-bold ${netPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {netPL >= 0 ? '+' : ''}{currentCountry.currency}{netPL.toFixed(0)}
                            </div>
                          </td>
                        )}
                        {isColumnVisible("result") && (
                          <td className="p-2 text-center">
                            {item.target_reached ? (
                              <Badge className="bg-green-900 border-green-500 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                TARGET
                              </Badge>
                            ) : isPartialTarget ? (
                              <Badge className="bg-yellow-900 border-yellow-600 text-xs">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                PARTIAL ({targetAchievedPct.toFixed(0)}%)
                              </Badge>
                            ) : isProfit ? (
                              <Badge className="bg-blue-900 border-blue-600 text-xs">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                PROFIT
                              </Badge>
                            ) : (
                              <Badge className="bg-red-900 border-red-600 text-xs">
                                <XCircle className="h-3 w-3 mr-1" />
                                LOSS
                              </Badge>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div className="text-center text-xs text-gray-500">
          <p>All calculations use actual market prices • Predicted prices shown for reference only</p>
        </div>
      </div>
    </div>
  );
}
